import { createLegacyCompiler } from './legacyCompiler';
import type { PreparedPlan, PromptRecord } from './promptCompiler';
import { logActivity } from './activityLogger';
export type GenerationResult = {slotId:string;promptId:string;success:boolean;imageUrl?:string;error?:string;metadata?:unknown};
export type GenerationProgress = {completed:number;total:number;currentSlots:string[];results:GenerationResult[]};

export async function generateSingleImage(prompt:PromptRecord, prepared:PreparedPlan):Promise<GenerationResult> {
  const startTime = Date.now();
  try {
    const engine=createLegacyCompiler(prepared.input,prepared.template);
    const response=await fetch('/api/generate',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(engine.buildImagePayload(prompt,engine.getSelectedAssets()))});
    const body=await response.json();
    void logActivity('api', 'generate_request', { slotId: prompt.slotId, success: response.ok, status: response.status, latencyMs: Date.now() - startTime });
    if (!response.ok) throw new Error(body.message || body.error || `Generation failed (${response.status}).`);
    const parts=body.candidates?.[0]?.content?.parts || [];
    const part=parts.find((item:{inlineData?:{data:string};inline_data?:{data:string}})=>item.inlineData?.data || item.inline_data?.data);
    if (!part) throw new Error(parts.find((item:{text?:string})=>item.text)?.text || body.promptFeedback?.blockReason || 'The backend returned no generated image.');
    const data=part.inlineData || part.inline_data;
    return {slotId:prompt.slotId,promptId:prompt.id,success:true,imageUrl:`data:${data.mimeType || data.mime_type || 'image/png'};base64,${data.data}`,metadata:body.usageMetadata};
  } catch (error) { void logActivity('api', 'generate_failed', { slotId: prompt.slotId, error: error instanceof Error ? error.message : 'Unknown', latencyMs: Date.now() - startTime }); return {slotId:prompt.slotId,promptId:prompt.id,success:false,error:error instanceof Error ? error.message : 'Generation failed'}; }
}

// A rolling two-worker queue: one failure never blocks siblings, and paid
// requests are never automatically retried. Each completion is emitted at once.
export async function generateImages(prompts:PromptRecord[], prepared:PreparedPlan, onProgress:(progress:GenerationProgress)=>void|Promise<void>) {
  const results = new Map<string,GenerationResult>(); const active=new Set<string>(); let cursor=0;
  let callbacks=Promise.resolve();
  const emit=()=>{
    const progress={completed:results.size,total:prompts.length,currentSlots:[...active],results:prompts.flatMap(prompt=>results.has(prompt.slotId)?[results.get(prompt.slotId)!]:[])};
    callbacks=callbacks.then(()=>onProgress(progress)); return callbacks;
  };
  await Promise.all(Array.from({length:Math.min(2,prompts.length)},async()=>{
    while(cursor<prompts.length){
      const prompt=prompts[cursor++]; active.add(prompt.slotId); await emit();
      const result=await generateSingleImage(prompt,prepared); results.set(prompt.slotId,result);active.delete(prompt.slotId);await emit();
    }
  }));
  return prompts.map(prompt=>results.get(prompt.slotId)!);
}
